import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Select, MenuItem, FormControl, InputLabel, IconButton,
  Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Alert, Grid, Switch, FormControlLabel,
} from '@mui/material';
import {
  Add, Delete, Edit, ArrowUpward, ArrowDownward, MusicNote, ContentCopy,
  Headphones, RecordVoiceOver, CheckCircle, ErrorOutline, SmartToy,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

export default function MixblockBuilder({ stationId, onSelect, selectedMixblockId }) {
  const [mixblocks, setMixblocks] = useState([]);
  const [selectedMixblock, setSelectedMixblock] = useState(null);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [categories, setCategories] = useState([]);
  const [mixAssets, setMixAssets] = useState([]);
  const [aiVoices, setAiVoices] = useState([]);
  const [aiPrompts, setAiPrompts] = useState([]);

  // Dialog states
  const [mixblockDialogOpen, setMixblockDialogOpen] = useState(false);
  const [editingMixblock, setEditingMixblock] = useState(null);
  const [mixblockForm, setMixblockForm] = useState({
    name: '', description: '', transition_bars: 0,
    target_lufs: -14.0,
  });
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const itemFormRef = useRef(null);
  const [itemForm, setItemForm] = useState({
    selection_mode: 'random_from_category',
    asset_id: '', category_id: '',
    sweeper_category_id: '', sweeper_asset_id: '',
    voicetrack_marker: false,
    ai_vt_marker: false,
    ai_prompt_id: null,
    ai_voice_id: null,
  });

  const fetchMixblocks = useCallback(async () => {
    if (!stationId) return;
    try {
      const res = await axios.get(`${API_URL}/api/beatmix/mixblocks`, {
        params: { station_id: stationId }, withCredentials: true,
      });
      setMixblocks(res.data);
    } catch (err) {
      console.error('Error fetching mixblocks:', err);
    }
  }, [stationId]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/categories`, { withCredentials: true });
      setCategories(res.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, []);

  const fetchMixAssets = useCallback(async () => {
    if (!stationId) return;
    try {
      const res = await axios.get(`${API_URL}/api/beatmix/assets`, {
        params: { station_id: stationId, has_cues_only: 'true' }, withCredentials: true,
      });
      setMixAssets(res.data);
    } catch (err) {
      console.error('Error fetching mix assets:', err);
    }
  }, [stationId]);

  const fetchMixblockDetail = useCallback(async (id) => {
    try {
      const res = await axios.get(`${API_URL}/api/beatmix/mixblocks/${id}`, { withCredentials: true });
      setSelectedMixblock(res.data);
    } catch (err) {
      console.error('Error fetching mixblock detail:', err);
    }
  }, []);

  const fetchAiVoices = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ai/voices`, { withCredentials: true });
      setAiVoices(res.data?.voices || res.data || []);
    } catch (err) {
      console.error('Error fetching AI voices:', err);
    }
  }, []);

  const fetchAiPrompts = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ai/voices/prompts`, { withCredentials: true });
      setAiPrompts(res.data || []);
    } catch (err) {
      console.error('Error fetching AI prompts:', err);
    }
  }, []);

  useEffect(() => { fetchMixblocks(); fetchCategories(); fetchMixAssets(); fetchAiVoices(); fetchAiPrompts(); }, [fetchMixblocks, fetchCategories, fetchMixAssets, fetchAiVoices, fetchAiPrompts]);
  useEffect(() => { if (selectedMixblockId) fetchMixblockDetail(selectedMixblockId); }, [selectedMixblockId, fetchMixblockDetail]);

  const handleSaveMixblock = async () => {
    if (!mixblockForm.name?.trim()) {
      setAlert({ type: 'error', message: 'Please enter a mixblock name.' });
      return;
    }
    if (!editingMixblock && !stationId) {
      setAlert({ type: 'error', message: 'Please select a station before creating a mixblock.' });
      return;
    }
    try {
      const payload = { ...mixblockForm, tempo_match_enabled: true };
      if (editingMixblock) {
        await axios.put(`${API_URL}/api/beatmix/mixblocks/${editingMixblock.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/beatmix/mixblocks`, { ...payload, station_id: stationId }, { withCredentials: true });
      }
      setMixblockDialogOpen(false);
      fetchMixblocks();
      setAlert({ type: 'success', message: editingMixblock ? 'Mixblock updated' : 'Mixblock created' });
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.message || 'Failed to save mixblock' });
    }
  };

  const handleDeleteMixblock = async (id) => {
    if (!window.confirm('Delete this mixblock?')) return;
    try {
      await axios.delete(`${API_URL}/api/beatmix/mixblocks/${id}`, { withCredentials: true });
      if (selectedMixblock?.id === id) setSelectedMixblock(null);
      fetchMixblocks();
      setAlert({ type: 'success', message: 'Mixblock deleted' });
    } catch (err) {
      setAlert({ type: 'error', message: 'Failed to delete mixblock' });
    }
  };

  const handleSaveItem = async () => {
    if (itemForm.ai_vt_marker) {
      if (!itemForm.ai_prompt_id) {
        setAlert({ type: 'error', message: 'Please select an AI Prompt for the AI Voicetrack marker.' });
        return;
      }
      if (!itemForm.ai_voice_id) {
        setAlert({ type: 'error', message: 'Please select an AI Voice for the AI Voicetrack marker.' });
        return;
      }
    }
    try {
      const settings = {
        voicetrack_marker: itemForm.voicetrack_marker || false,
        ai_vt_marker: itemForm.ai_vt_marker || false,
      };
      if (itemForm.ai_vt_marker) {
        settings.ai_prompt_id = parseInt(itemForm.ai_prompt_id);
        settings.ai_voice_id = parseInt(itemForm.ai_voice_id);
      }
      const data = {
        selection_mode: itemForm.selection_mode,
        asset_id: itemForm.asset_id || null,
        category_id: itemForm.category_id || null,
        sweeper_category_id: itemForm.sweeper_category_id || null,
        sweeper_asset_id: itemForm.sweeper_asset_id || null,
        voicetrack_asset_id: null,
        voicetrack_category_id: null,
        settings,
      };
      if (editingItem) {
        await axios.put(`${API_URL}/api/beatmix/mixblocks/${selectedMixblock.id}/items/${editingItem.id}`, data, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/beatmix/mixblocks/${selectedMixblock.id}/items`, data, { withCredentials: true });
      }
      setItemDialogOpen(false);
      fetchMixblockDetail(selectedMixblock.id);
      setAlert({ type: 'success', message: editingItem ? 'Item updated' : 'Item added' });
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.message || 'Failed to save item' });
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await axios.delete(`${API_URL}/api/beatmix/mixblocks/${selectedMixblock.id}/items/${itemId}`, { withCredentials: true });
      fetchMixblockDetail(selectedMixblock.id);
    } catch (err) {
      setAlert({ type: 'error', message: 'Failed to delete item' });
    }
  };

  const handleDuplicateItem = async (item) => {
    try {
      const itemSettings = item.settings ? (typeof item.settings === 'string' ? JSON.parse(item.settings) : item.settings) : {};
      const data = {
        selection_mode: item.selection_mode,
        asset_id: item.asset_id || null,
        category_id: item.category_id || null,
        sweeper_category_id: item.sweeper_category_id || null,
        sweeper_asset_id: item.sweeper_asset_id || null,
        voicetrack_asset_id: null,
        voicetrack_category_id: null,
        settings: itemSettings,
      };
      await axios.post(`${API_URL}/api/beatmix/mixblocks/${selectedMixblock.id}/items`, data, { withCredentials: true });
      fetchMixblockDetail(selectedMixblock.id);
      setAlert({ type: 'success', message: 'Item duplicated' });
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.message || 'Failed to duplicate item' });
    }
  };

  const handleMoveItem = async (itemId, direction) => {
    if (!selectedMixblock?.items) return;
    const items = [...selectedMixblock.items];
    const idx = items.findIndex(i => i.id === itemId);
    if (idx < 0) return;
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= items.length) return;
    [items[idx], items[newIdx]] = [items[newIdx], items[idx]];
    const itemOrder = items.map((item, i) => ({ id: item.id, position: i }));
    try {
      await axios.put(`${API_URL}/api/beatmix/mixblocks/${selectedMixblock.id}/reorder`, { itemOrder }, { withCredentials: true });
      fetchMixblockDetail(selectedMixblock.id);
    } catch (err) {
      setAlert({ type: 'error', message: 'Failed to reorder' });
    }
  };

  const openItemDialog = (item = null) => {
    setEditingItem(item);
    const itemSettings = item?.settings
      ? (typeof item.settings === 'string' ? JSON.parse(item.settings) : item.settings)
      : {};
    setItemForm({
      selection_mode: item?.selection_mode || 'random_from_category',
      asset_id: item?.asset_id || '',
      category_id: item?.category_id || '',
      sweeper_category_id: item?.sweeper_category_id || '',
      sweeper_asset_id: item?.sweeper_asset_id || '',
      voicetrack_marker: itemSettings.voicetrack_marker || false,
      ai_vt_marker: itemSettings.ai_vt_marker || false,
      ai_prompt_id: itemSettings.ai_prompt_id || null,
      ai_voice_id: itemSettings.ai_voice_id || null,
    });
    setItemDialogOpen(true);
    setTimeout(() => itemFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
  };

  const cardSx = { bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)', borderRadius: 2 };
  const tableCellSx = { color: 'var(--rs-text)', borderBottom: '1px solid var(--rs-border)' };

  return (
    <Box>
      {alert && (
        <Alert severity={alert.type} onClose={() => setAlert(null)} sx={{ mb: 2 }}>{alert.message}</Alert>
      )}

      <Grid container spacing={2}>
        {/* Mixblock List */}
        <Grid item xs={12} md={4}>
          <Card sx={cardSx}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ color: '#22d3ee' }}>Mixblocks</Typography>
                <Button size="small" startIcon={<Add />} variant="contained"
                  disabled={!stationId}
                  onClick={() => {
                    setEditingMixblock(null);
                    setMixblockForm({ name: '', description: '', transition_bars: 0, target_lufs: -14.0 });
                    setMixblockDialogOpen(true);
                  }}
                  sx={{ bgcolor: '#7c4dff' }}>
                  New
                </Button>
              </Box>
              {mixblocks.length === 0 ? (
                <Typography color="text.secondary" variant="body2">No mixblocks created yet</Typography>
              ) : mixblocks.map(mb => (
                <Box key={mb.id} onClick={() => { fetchMixblockDetail(mb.id); if (onSelect) onSelect(mb.id); }}
                  sx={{
                    p: 1, mb: 1, borderRadius: 1, cursor: 'pointer',
                    bgcolor: selectedMixblock?.id === mb.id ? 'rgba(124,77,255,0.15)' : 'var(--rs-row)',
                    border: selectedMixblock?.id === mb.id ? '1px solid #7c4dff' : '1px solid transparent',
                    '&:hover': { bgcolor: 'rgba(124,77,255,0.08)' },
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'var(--rs-text)' }}>{mb.name}</Typography>
                    {mb.description && <Typography variant="caption" color="text.secondary">{mb.description}</Typography>}
                  </Box>
                  <Box>
                    <IconButton size="small" onClick={(e) => {
                      e.stopPropagation();
                      setEditingMixblock(mb);
                      setMixblockForm({ name: mb.name, description: mb.description || '', transition_bars: mb.transition_bars || 0, target_lufs: mb.target_lufs || -14.0 });
                      setMixblockDialogOpen(true);
                    }} sx={{ color: '#22d3ee' }}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteMixblock(mb.id); }}
                      sx={{ color: '#f44336' }}><Delete fontSize="small" /></IconButton>
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Mixblock Detail / Item Builder */}
        <Grid item xs={12} md={8}>
          {selectedMixblock ? (
            <Card sx={cardSx}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ color: '#22d3ee' }}>{selectedMixblock.name}</Typography>
                  </Box>
                  <Button size="small" startIcon={<Add />} variant="contained" onClick={() => openItemDialog()}
                    sx={{ bgcolor: '#7c4dff' }}>Add Track</Button>
                </Box>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...tableCellSx, fontWeight: 'bold', width: 40 }}>#</TableCell>
                        <TableCell sx={{ ...tableCellSx, fontWeight: 'bold' }}>Track / Category</TableCell>
                        <TableCell sx={{ ...tableCellSx, fontWeight: 'bold' }}>Cues</TableCell>
                        <TableCell sx={{ ...tableCellSx, fontWeight: 'bold', width: 160 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(!selectedMixblock.items || selectedMixblock.items.length === 0) ? (
                        <TableRow>
                          <TableCell colSpan={4} sx={{ ...tableCellSx, textAlign: 'center', color: '#999' }}>
                            No tracks added. Click "Add Track" to build the mixblock.
                          </TableCell>
                        </TableRow>
                      ) : selectedMixblock.items.map((item, idx) => {
                        const hasSweeper = item.sweeper_category_name || item.sweeper_asset_title;
                        const sweeperLabel = item.sweeper_category_name || item.sweeper_asset_title;
                        const itemSettings = item.settings ? (typeof item.settings === 'string' ? JSON.parse(item.settings) : item.settings) : {};
                        const hasVt = itemSettings.voicetrack_marker;
                        const hasAiVt = itemSettings.ai_vt_marker;
                        const hasTransitions = hasSweeper || hasVt || hasAiVt;
                        const aiPromptName = hasAiVt && itemSettings.ai_prompt_id
                          ? (aiPrompts.find(p => p.id === itemSettings.ai_prompt_id)?.name || `Prompt #${itemSettings.ai_prompt_id}`)
                          : null;
                        const aiVoiceName = hasAiVt && itemSettings.ai_voice_id
                          ? (aiVoices.find(v => v.id === itemSettings.ai_voice_id)?.name || `Voice #${itemSettings.ai_voice_id}`)
                          : null;


                        return (
                        <TableRow key={item.id} sx={{ '&:hover': { bgcolor: 'var(--rs-row)' } }}>
                          <TableCell sx={tableCellSx}>{idx + 1}</TableCell>
                          <TableCell sx={tableCellSx}>
                            {item.selection_mode === 'specific' ? (
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.asset_title || `Asset #${item.asset_id}`}</Typography>
                                {item.asset_artist && (
                                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.3 }}>{item.asset_artist}</Typography>
                                )}
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {item.category_name || 'Random from category'}
                              </Typography>
                            )}

                            {hasTransitions && (
                              <Box sx={{ mt: 0.25, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
                                {hasSweeper && (
                                  <Typography variant="caption" sx={{ color: '#ff9800', fontSize: '0.7rem', display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                                    <Headphones sx={{ fontSize: 12 }} /> {sweeperLabel}
                                  </Typography>
                                )}
                                {hasSweeper && (hasVt || hasAiVt) && (
                                  <Typography variant="caption" sx={{ color: 'var(--rs-muted)', fontSize: '0.65rem' }}>·</Typography>
                                )}
                                {hasVt && (
                                  <Typography variant="caption" sx={{ color: '#4caf50', fontSize: '0.7rem', display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                                    <RecordVoiceOver sx={{ fontSize: 12 }} /> VT
                                  </Typography>
                                )}
                                {hasVt && hasAiVt && (
                                  <Typography variant="caption" sx={{ color: 'var(--rs-muted)', fontSize: '0.65rem' }}>·</Typography>
                                )}
                                {hasAiVt && (
                                  <Typography variant="caption" sx={{ color: '#ab47bc', fontSize: '0.7rem', display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                                    <SmartToy sx={{ fontSize: 12 }} /> AI VT{aiPromptName ? ` · ${aiPromptName}` : ''}{aiVoiceName ? ` / ${aiVoiceName}` : ''}
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </TableCell>
                          <TableCell sx={tableCellSx}>
                            {item.selection_mode === 'specific' ? (
                              item.has_mix_cues ? (
                                <Chip label="Yes" icon={<CheckCircle />} size="small" color="success" variant="outlined" />
                              ) : (
                                <Chip label="No" icon={<ErrorOutline />} size="small" color="warning" variant="outlined" />
                              )
                            ) : (
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>Auto</Typography>
                            )}
                          </TableCell>
                          <TableCell sx={tableCellSx}>
                            <IconButton size="small" onClick={() => handleMoveItem(item.id, 'up')} disabled={idx === 0}
                              sx={{ color: '#22d3ee' }}><ArrowUpward fontSize="small" /></IconButton>
                            <IconButton size="small" onClick={() => handleMoveItem(item.id, 'down')}
                              disabled={idx === selectedMixblock.items.length - 1}
                              sx={{ color: '#22d3ee' }}><ArrowDownward fontSize="small" /></IconButton>
                            <IconButton size="small" onClick={() => openItemDialog(item)}
                              sx={{ color: '#22d3ee' }}><Edit fontSize="small" /></IconButton>
                            <IconButton size="small" onClick={() => handleDuplicateItem(item)}
                              sx={{ color: '#22d3ee' }}><ContentCopy fontSize="small" /></IconButton>
                            <IconButton size="small" onClick={() => handleDeleteItem(item.id)}
                              sx={{ color: '#f44336' }}><Delete fontSize="small" /></IconButton>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Inline Item Add/Edit Form */}
                {itemDialogOpen && (
                  <Paper ref={itemFormRef} sx={{ mt: 2, p: 2, bgcolor: 'rgba(124,77,255,0.08)', border: '1px solid #7c4dff', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'var(--rs-text)' }}>
                      {editingItem ? 'Edit Track Entry' : 'Add Track Entry'}
                    </Typography>

                    {/* Track Selection */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" sx={{ color: '#22d3ee', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>
                        Track
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={5}>
                          <FormControl fullWidth size="small">
                            <InputLabel sx={{ color: '#999' }}>Selection Mode</InputLabel>
                            <Select value={itemForm.selection_mode} label="Selection Mode"
                              onChange={(e) => setItemForm({ ...itemForm, selection_mode: e.target.value })}
                              sx={{ color: 'var(--rs-text)' }}>
                              <MenuItem value="specific">Specific Track</MenuItem>
                              <MenuItem value="random_from_category">Random from Category</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={7}>
                          {itemForm.selection_mode === 'specific' ? (
                            <FormControl fullWidth size="small">
                              <InputLabel sx={{ color: '#999' }}>Track</InputLabel>
                              <Select value={itemForm.asset_id || ''} label="Track"
                                onChange={(e) => setItemForm({ ...itemForm, asset_id: e.target.value })}
                                sx={{ color: 'var(--rs-text)' }}>
                                <MenuItem value="">-- Select --</MenuItem>
                                {mixAssets.map(a => (
                                  <MenuItem key={a.id} value={a.id}>
                                    {a.title} {a.artist ? `- ${a.artist}` : ''} {a.mix_bpm ? `(${a.mix_bpm.toFixed(0)} BPM)` : ''}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : (
                            <FormControl fullWidth size="small">
                              <InputLabel sx={{ color: '#999' }}>Music Category</InputLabel>
                              <Select value={itemForm.category_id || ''} label="Music Category"
                                onChange={(e) => setItemForm({ ...itemForm, category_id: e.target.value })}
                                sx={{ color: 'var(--rs-text)' }}>
                                <MenuItem value="">-- Select --</MenuItem>
                                {categories.filter(c => c.category_type === 'music').map(c => (
                                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Transition Options */}
                    <Box sx={{ p: 1.5, bgcolor: 'var(--rs-row)', border: '1px solid var(--rs-border)', borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ color: '#ff9800', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>
                        Over Transition (optional)
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                        These play over the crossfade between this track and the next, finishing at the end of the mix point.
                      </Typography>

                      {/* Sweeper */}
                      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel sx={{ color: '#999' }}>Sweeper Category</InputLabel>
                        <Select value={itemForm.sweeper_category_id || ''} label="Sweeper Category"
                          onChange={(e) => setItemForm({ ...itemForm, sweeper_category_id: e.target.value, sweeper_asset_id: '' })}
                          sx={{ color: 'var(--rs-text)' }}>
                          <MenuItem value="">None</MenuItem>
                          {categories.filter(c => c.category_type === 'station_id').map(c => (
                            <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {/* VT Marker */}
                      <Box sx={{ mb: 1 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={itemForm.voicetrack_marker || false}
                              onChange={(e) => setItemForm({ ...itemForm, voicetrack_marker: e.target.checked })}
                              size="small"
                              sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4caf50' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4caf50' } }}
                            />
                          }
                          label={<Typography variant="body2" sx={{ color: itemForm.voicetrack_marker ? '#4caf50' : '#999' }}>Voicetrack Marker</Typography>}
                        />
                      </Box>

                      {/* AI VT Marker */}
                      <Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={itemForm.ai_vt_marker || false}
                              onChange={(e) => setItemForm({ ...itemForm, ai_vt_marker: e.target.checked, ...(!e.target.checked ? { ai_prompt_id: null, ai_voice_id: null } : {}) })}
                              size="small"
                              sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#ab47bc' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#ab47bc' } }}
                            />
                          }
                          label={<Typography variant="body2" sx={{ color: itemForm.ai_vt_marker ? '#ab47bc' : '#999' }}>AI Voicetrack Marker</Typography>}
                        />
                        {itemForm.ai_vt_marker && (
                          <Grid container spacing={2} sx={{ mt: 0.5 }}>
                            <Grid item xs={12} sm={6}>
                              <FormControl fullWidth size="small">
                                <InputLabel sx={{ color: '#999' }}>AI Prompt *</InputLabel>
                                <Select value={itemForm.ai_prompt_id || ''} label="AI Prompt *"
                                  onChange={(e) => setItemForm({ ...itemForm, ai_prompt_id: e.target.value || null })}
                                  sx={{ color: 'var(--rs-text)' }}>
                                  <MenuItem value="">Select Prompt</MenuItem>
                                  {aiPrompts.length === 0 ? (
                                    <MenuItem disabled>No prompts available</MenuItem>
                                  ) : aiPrompts.map(p => (
                                    <MenuItem key={p.id} value={p.id}>
                                      #{p.id} · {p.name} {p.folder_name && `(${p.folder_name})`}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <FormControl fullWidth size="small">
                                <InputLabel sx={{ color: '#999' }}>AI Voice *</InputLabel>
                                <Select value={itemForm.ai_voice_id || ''} label="AI Voice *"
                                  onChange={(e) => setItemForm({ ...itemForm, ai_voice_id: e.target.value || null })}
                                  sx={{ color: 'var(--rs-text)' }}>
                                  <MenuItem value="">Select Voice</MenuItem>
                                  {aiVoices.length === 0 ? (
                                    <MenuItem disabled>No voices available</MenuItem>
                                  ) : aiVoices.map(v => (
                                    <MenuItem key={v.id} value={v.id}>
                                      {v.name} ({v.voice_type === 'speechify' ? 'Speechify' : (v.voice_type === 'elevenlabs' ? 'ElevenLabs' : 'Cloned')})
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                          </Grid>
                        )}
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                      <Button size="small" onClick={() => setItemDialogOpen(false)} sx={{ color: '#999' }}>Cancel</Button>
                      <Button size="small" variant="contained" onClick={handleSaveItem} sx={{ bgcolor: '#7c4dff' }}>
                        {editingItem ? 'Update' : 'Add'}
                      </Button>
                    </Box>
                  </Paper>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card sx={cardSx}>
              <CardContent>
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  Select a mixblock or create a new one to start building
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Mixblock Create/Edit Dialog */}
      <Dialog open={mixblockDialogOpen} onClose={() => setMixblockDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--rs-paper)', color: 'var(--rs-text)' } }}>
        <DialogTitle>{editingMixblock ? 'Edit Mixblock' : 'Create Mixblock'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Name" value={mixblockForm.name} sx={{ mt: 1, mb: 2, '& .MuiInputBase-root': { color: 'var(--rs-text)' } }}
            onChange={(e) => setMixblockForm({ ...mixblockForm, name: e.target.value })} />
          <TextField fullWidth label="Description" value={mixblockForm.description} sx={{ mb: 2, '& .MuiInputBase-root': { color: 'var(--rs-text)' } }}
            onChange={(e) => setMixblockForm({ ...mixblockForm, description: e.target.value })} />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField fullWidth label="Transition Bars (0=auto)" type="number" value={mixblockForm.transition_bars}
                sx={{ '& .MuiInputBase-root': { color: 'var(--rs-text)' } }}
                onChange={(e) => setMixblockForm({ ...mixblockForm, transition_bars: parseInt(e.target.value) || 0 })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Target LUFS" type="number" value={mixblockForm.target_lufs}
                sx={{ '& .MuiInputBase-root': { color: 'var(--rs-text)' } }}
                inputProps={{ step: 0.5 }}
                onChange={(e) => setMixblockForm({ ...mixblockForm, target_lufs: parseFloat(e.target.value) || -14.0 })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMixblockDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveMixblock} sx={{ bgcolor: '#7c4dff' }}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
